/**
 * Step 2 — DB 커넥터 소스 설정
 *
 * Phase 1: 연결 정보 입력 + 연결 테스트
 * Phase 2: 스키마/테이블 선택
 * Phase 3: 컬럼 한글 설명 편집 (LLM 자동생성 + 인라인 편집)
 */

import { useState, useEffect } from 'react';
import { DbConfig, DbTableSelection, DbColumnMeta, SqlHint, DEFAULT_SQL_SYSTEM_PROMPT, DEFAULT_SQL_SUMMARY_PROMPT } from '../shared/pipeline.types';
import { knowledgeBaseApi } from '../../../api';
import { Button, Select, Tabs } from '../../ui';
import CheckIcon from '../../../assets/icons/common/check.svg?react';
import EyeIcon from '../../../assets/icons/common/eye.svg?react';
import EyeOffIcon from '../../../assets/icons/common/eye-off.svg?react';

// DB 유형별 기본 포트
const DEFAULT_PORTS: Record<DbConfig['type'], number> = {
  mysql: 3306,
  postgresql: 5432,
  redshift: 5439,
  oracle: 1521,
  mssql: 1433,
};

// DB 유형 레이블
const DB_TYPE_LABELS: Record<DbConfig['type'], string> = {
  mysql: 'MySQL',
  postgresql: 'PostgreSQL',
  redshift: 'Amazon Redshift',
  oracle: 'Oracle',
  mssql: 'MS SQL Server',
};

// DB 유형 카드 표시용 (약자 아이콘 + 짧은 이름)
const DB_TYPE_CARDS: Record<DbConfig['type'], { abbr: string; name: string }> = {
  mysql: { abbr: 'My', name: 'MySQL' },
  postgresql: { abbr: 'Pg', name: 'PostgreSQL' },
  redshift: { abbr: 'Rs', name: 'Redshift' },
  oracle: { abbr: 'Or', name: 'Oracle' },
  mssql: { abbr: 'MS', name: 'SQL Server' },
};

// 연결 상태
type ConnectStatus = 'idle' | 'testing' | 'success' | 'error';

interface DatabaseSourceStepProps {
  config: DbConfig;
  onChange: (config: DbConfig) => void;
  /** true면 기존 설정 수정 모드 — 탭 UI로 표시 */
  isEditing?: boolean;
}

export function DatabaseSourceStep({ config, onChange, isEditing = false }: DatabaseSourceStepProps) {
  const set = <K extends keyof DbConfig>(key: K, value: DbConfig[K]) =>
    onChange({ ...config, [key]: value });

  // 수정 모드 탭 상태
  type EditTab = 'connection' | 'tables' | 'hints' | 'prompts';
  const [activeTab, setActiveTab] = useState<EditTab>('connection');

  const handleTypeChange = (type: DbConfig['type']) => {
    // 유형 변경 시 포트 자동 변경 + 기존 테이블 목록 초기화
    onChange({ ...config, type, port: DEFAULT_PORTS[type], tables: [] });
    setConnectStatus('idle');
    setConnectError('');
    setPhase(1);
  };

  // ── 연결 정보 변경 감지: host/database/username 변경 시 테이블 리셋 ──
  const handleConnectionFieldChange = <K extends keyof DbConfig>(key: K, value: DbConfig[K]) => {
    const connFields: (keyof DbConfig)[] = ['host', 'port', 'database', 'schema', 'username', 'password'];
    if (connFields.includes(key)) {
      // 연결 관련 필드 변경 → 이전 테이블 목록·연결 상태 초기화
      onChange({ ...config, [key]: value, tables: [] });
      setConnectStatus('idle');
      setConnectError('');
      if (phase > 1) setPhase(1);
    } else {
      onChange({ ...config, [key]: value });
    }
  };

  // ── 단계 상태 ──
  // 기존 테이블 데이터가 있으면 (재설정 모드) phase 2부터 시작
  const [phase, setPhase] = useState<1 | 2 | 3 | 4 | 5>(config.tables && config.tables.length > 0 ? 2 : 1);
  const [connectStatus, setConnectStatus] = useState<ConnectStatus>(config.tables && config.tables.length > 0 ? 'success' : 'idle');
  const [connectError, setConnectError] = useState('');
  // 비밀번호 보기 토글 (화면 표시용)
  const [showPassword, setShowPassword] = useState(false);

  // 선택된 테이블 (phase 3 진입 시 사용)
  const [editingTable, setEditingTable] = useState<string | null>(null);

  // LLM 생성 중인 테이블
  const [generatingLabels, setGeneratingLabels] = useState<Set<string>>(new Set());

  // ── 재설정 모드: 마운트 시 자동으로 전체 테이블 목록 새로고침 ──
  const [refreshingTables, setRefreshingTables] = useState(false);

  // 기존 선택 정보는 유지하면서 전체 목록을 다시 불러옴
  useEffect(() => {
    const hasExistingTables = config.tables && config.tables.length > 0;
    const hasConnectionInfo = config.host && config.database && config.username;
    if (!hasExistingTables || !hasConnectionInfo) return;

    setRefreshingTables(true);
    (async () => {
      try {
        const result = await knowledgeBaseApi.dbTest({
          type: config.type,
          host: config.host,
          port: config.port,
          database: config.database,
          schema: config.schema,
          username: config.username,
          password: config.password,
        });
        if (result.success) {
          // 기존 선택/레이블 정보를 유지하면서 전체 테이블 목록 병합
          const prevMap = new Map(
            config.tables.map((t: DbTableSelection) => [`${t.schema}.${t.table}`, t])
          );
          const merged: DbTableSelection[] = result.tables.map((t: { schema: string; table: string; columns: DbColumnMeta[] }) => {
            const prev = prevMap.get(`${t.schema}.${t.table}`);
            return {
              schema: t.schema,
              table: t.table,
              selected: prev?.selected ?? false,
              labelGenerated: prev?.labelGenerated ?? false,
              columns: t.columns.map((c: DbColumnMeta, ci: number) => ({
                ...c,
                label: prev?.columns[ci]?.label ?? c.comment ?? '',
                include: prev?.columns[ci]?.include ?? true,
              })),
            };
          });
          onChange({ ...config, tables: merged });
          setConnectStatus('success');
        }
      } catch {
        // 실패해도 기존 데이터로 유지
      } finally {
        setRefreshingTables(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── 연결 테스트 ──
  const handleTestConnection = async () => {
    if (!config.host || !config.database || !config.username) return;
    setConnectStatus('testing');
    setConnectError('');
    try {
      const result = await knowledgeBaseApi.dbTest({
        type: config.type,
        host: config.host,
        port: config.port,
        database: config.database,
        schema: config.schema,
        username: config.username,
        password: config.password,
      });
      if (result.success) {
        // 테이블 목록을 config에 반영
        const tables: DbTableSelection[] = result.tables.map((t: { schema: string; table: string; columns: DbColumnMeta[] }) => ({
          schema: t.schema,
          table: t.table,
          selected: false,
          labelGenerated: false,
          columns: t.columns.map((c: DbColumnMeta) => ({ ...c, label: c.comment || '', include: true })),
        }));
        onChange({ ...config, tables });
        setConnectStatus('success');
        setPhase(2);
      } else {
        setConnectStatus('error');
        setConnectError(result.error || '연결에 실패했습니다.');
      }
    } catch (e: unknown) {
      setConnectStatus('error');
      setConnectError(e instanceof Error ? e.message : '연결에 실패했습니다.');
    }
  };

  // ── LLM 한글 설명 자동 생성 ──
  const handleGenerateLabels = async (schemaName: string, tableName: string) => {
    const key = `${schemaName}.${tableName}`;
    setGeneratingLabels(prev => new Set(prev).add(key));
    try {
      const tableIdx = config.tables.findIndex(t => t.schema === schemaName && t.table === tableName);
      if (tableIdx < 0) return;
      const columns = config.tables[tableIdx].columns.map(c => ({ name: c.name, type: c.type, comment: c.comment }));
      const result = await knowledgeBaseApi.dbGenerateLabels({ columns });
      const newTables = config.tables.map((t, i) =>
        i === tableIdx
          ? {
              ...t,
              labelGenerated: true,
              columns: t.columns.map((c, ci) => ({
                ...c,
                label: result.labels[ci] ?? c.label,
              })),
            }
          : t
      );
      onChange({ ...config, tables: newTables });
    } catch {
      // 실패해도 사용자가 직접 편집 가능
    } finally {
      setGeneratingLabels(prev => { const s = new Set(prev); s.delete(key); return s; });
    }
  };

  // ── 컬럼 레이블 편집 ──
  const handleLabelChange = (tableIdx: number, colIdx: number, label: string) => {
    const newTables = config.tables.map((t, ti) =>
      ti === tableIdx
        ? { ...t, columns: t.columns.map((c, ci) => (ci === colIdx ? { ...c, label } : c)) }
        : t
    );
    onChange({ ...config, tables: newTables });
  };

  const handleIncludeChange = (tableIdx: number, colIdx: number, include: boolean) => {
    const newTables = config.tables.map((t, ti) =>
      ti === tableIdx
        ? { ...t, columns: t.columns.map((c, ci) => (ci === colIdx ? { ...c, include } : c)) }
        : t
    );
    onChange({ ...config, tables: newTables });
  };

  // ── 테이블 선택 토글 ──
  const handleTableToggle = (tableIdx: number) => {
    const newTables = config.tables.map((t, i) =>
      i === tableIdx ? { ...t, selected: !t.selected } : t
    );
    onChange({ ...config, tables: newTables });
  };

  const selectedCount = config.tables.filter(t => t.selected).length;

  // ──────────────────────────────────────────
  // Phase 1: 연결 정보
  // ──────────────────────────────────────────
  const renderPhase1 = () => (
    <div className="db-phase">
      <div className="db-phase-header d-flex justify-between">
        <div className='d-inline-flex items-center gap-4'>
          <span className="db-phase-badge">1</span>
          <span className="db-phase-title">연결 정보</span>
        </div>
        {connectStatus === 'success' && (
          <div className='db-next-btn-wrap'>
            <span className='red-color'>(필수)</span>
            <button color="primary" className="db-next-btn" onClick={() => setPhase(2)}>
              테이블 선택 →
            </button>
          </div>
        )}
      </div>

      <div className="db-form form-grid">
        {/* DB 커넥터는 Text-to-SQL 단일 모드로 고정 */}

        {/* 샘플 데이터 행 수 옵션 */}
        <div className="form-item">
          <div className='db-phase-title-wrap mb-10'>
            <h4 className="db-phase-title">
              샘플 데이터 행 수
            </h4>
            <p className="db-phase-subtitle">LLM이 컬럼의 실제 데이터 형식을 파악하는 데 활용됩니다. 민감한 데이터가 있으면 0으로 설정하세요.</p>
          </div>
          <Select
            className="pipe-settings-select"
            value={config.sampleRows}
            onChange={e => set('sampleRows', parseInt(e.target.value))}
            options={[
              { value: '0', label: '포함 안함' },
              { value: '3', label: '3행' },
              { value: '5', label: '5행' },
              { value: '10', label: '10행' }
            ]}
          />
        </div>
        {/* DB 유형 - 약자 아이콘 카드 */}
        <div className="form-item">
          <label className="form-label">DB 유형</label>
          <div className="db-type-cards">
            {(Object.keys(DB_TYPE_LABELS) as DbConfig['type'][]).map(t => (
              <button
                key={t}
                type="button"
                className={`db-type-card${config.type === t ? ' active' : ''}`}
                onClick={() => handleTypeChange(t)}
                title={DB_TYPE_LABELS[t]}
              >
                <span className="db-type-abbr">{DB_TYPE_CARDS[t].abbr}</span>
                <span className="db-type-name">{DB_TYPE_CARDS[t].name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* 연결 정보 - 왼쪽 구분 라벨 / 오른쪽 입력 */}
        <div className="db-conn-rows">
          {/* 서버 */}
          <div className="db-conn-row">
            <div className="db-conn-row-head">
              <div className="db-conn-row-title">서버</div>
              <div className="db-conn-row-desc">접속 주소</div>
            </div>
            <div className="db-conn-row-fields db-conn-row-fields--full">
              <div className="form-item">
                <label className="form-label">호스트 : 포트 <span className="required">*</span></label>
                <div className="db-hostport">
                  <input
                    type="text"
                    className="pipe-settings-input db-hostport-host"
                    placeholder="localhost 또는 your-cluster.redshift.amazonaws.com"
                    value={config.host}
                    onChange={e => handleConnectionFieldChange('host', e.target.value)}
                  />
                  <span className="db-hostport-sep">:</span>
                  <input
                    type="number"
                    className="pipe-settings-input db-hostport-port"
                    value={config.port}
                    onChange={e => handleConnectionFieldChange('port', parseInt(e.target.value) || DEFAULT_PORTS[config.type])}
                    aria-label="포트"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* 대상 */}
          <div className="db-conn-row">
            <div className="db-conn-row-head">
              <div className="db-conn-row-title">대상</div>
              <div className="db-conn-row-desc">읽어올 범위</div>
            </div>
            <div className="db-conn-row-fields">
              <div className="form-item">
                <label className="form-label">데이터베이스 <span className="required">*</span></label>
                <input
                  type="text"
                  className="pipe-settings-input"
                  placeholder="production_db"
                  value={config.database}
                  onChange={e => handleConnectionFieldChange('database', e.target.value)}
                />
              </div>
              <div className="form-item">
                <label className="form-label">스키마 <span className="optional">(선택)</span></label>
                <input
                  type="text"
                  className="pipe-settings-input"
                  placeholder="public"
                  value={config.schema}
                  onChange={e => handleConnectionFieldChange('schema', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* 인증 */}
          <div className="db-conn-row">
            <div className="db-conn-row-head">
              <div className="db-conn-row-title">인증</div>
              <div className="db-conn-row-desc">읽기 전용 계정 권장</div>
            </div>
            <div className="db-conn-row-fields">
              <div className="form-item">
                <label className="form-label">사용자명 <span className="required">*</span></label>
                <input
                  type="text"
                  className="pipe-settings-input"
                  placeholder="DB 사용자명"
                  value={config.username}
                  onChange={e => handleConnectionFieldChange('username', e.target.value)}
                />
              </div>
              <div className="form-item">
                <label className="form-label">비밀번호</label>
                <div className="db-password">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="pipe-settings-input"
                    placeholder="DB 비밀번호"
                    value={config.password}
                    onChange={e => handleConnectionFieldChange('password', e.target.value)}
                  />
                  <button
                    type="button"
                    className="db-password-toggle"
                    onClick={() => setShowPassword(v => !v)}
                    aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
                  >
                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 연결 상태 바 + 연결 테스트 / 다음 */}
        <div className={`db-conn-status is-${connectStatus}`}>
          <span className="db-conn-status-icon">
            {connectStatus === 'testing' ? <span className="db-spinner" />
              : connectStatus === 'success' ? <CheckIcon />
              : connectStatus === 'error' ? '!'
              : '?'}
          </span>
          <div className="db-conn-status-text">
            <div className="db-conn-status-title">
              {connectStatus === 'testing' ? '연결 중...'
                : connectStatus === 'success' ? '연결 성공'
                : connectStatus === 'error' ? '연결 실패'
                : '연결 테스트가 필요합니다'}
            </div>
            <div className="db-conn-status-desc">
              {connectStatus === 'success' ? `- ${DB_TYPE_CARDS[config.type].name} · 정보를 바꾸면 다시 테스트해 주세요`
                : connectStatus === 'error' ? `- ${connectError}` || `'- 연결 정보를 확인한 뒤 다시 시도해 주세요'`
                : connectStatus === 'testing' ? `- ${DB_TYPE_CARDS[config.type].name} 서버에 연결하고 있습니다`
                : '- 필수 정보를 입력하고 연결을 확인해 주세요'}
            </div>
          </div>
          <div className="db-conn-status-actions">
            <Button
              color={connectStatus === 'idle' ? 'primary' : 'gray'}
              appearance={connectStatus === 'idle' ? 'fill' : 'outline'}
              onClick={handleTestConnection}
              disabled={connectStatus === 'testing' || !config.host || !config.database || !config.username}
              size='sm'
            >
              {connectStatus === 'idle' ? '연결 테스트' : '다시 테스트'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  // ──────────────────────────────────────────
  // Phase 2: 테이블 선택
  // ──────────────────────────────────────────
  const renderPhase2 = () => {
    // 스키마별로 그룹화
    const schemaGroups = config.tables.reduce<Record<string, DbTableSelection[]>>((acc, t) => {
      const s = t.schema || '(default)';
      if (!acc[s]) acc[s] = [];
      acc[s].push(t);
      return acc;
    }, {});

    return (
      <div className="db-phase">
        <div className="db-phase-header d-flex justify-between">
          <div className='d-inline-flex items-center gap-4'>
          <button className="db-back-btn" onClick={() => setPhase(1)}>← 연결 정보</button>
            <span className="db-phase-badge">2</span>
            <span className="db-phase-title">테이블 선택</span>
            <div className="count-box">
              <span className='count'>{selectedCount}</span>개 선택됨 / 전체 {config.tables.length}개
            </div>
          </div>
          <div className='db-next-btn-wrap'>
            <span>(선택)</span>
            <button className="db-next-btn" onClick={() => setPhase(3)}>
              SQL 힌트 설정 →
            </button>
          </div>
        </div>
        <div className='db-form'>
          {refreshingTables ? (
            <div className="db-empty" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span className="db-spinner" />
              테이블 목록 불러오는 중...
            </div>
          ) : config.tables.length === 0 ? (
            <div className="db-empty">조회된 테이블이 없습니다.</div>
          ) : (
            <div className="db-table-list">
              {Object.entries(schemaGroups).map(([schema, tables]) => (
                <div key={schema} className="db-schema-group">
                  {Object.keys(schemaGroups).length > 1 && (
                    <div className="db-schema-label">{schema}</div>
                  )}
                  {tables.map((t) => {
                    const idx = config.tables.findIndex(x => x.schema === t.schema && x.table === t.table);
                    return (
                      <div
                        key={t.table}
                        className={`db-table-row${t.selected ? ' selected' : ''}`}
                      >
                        <label className="db-table-check">
                          <input
                            type="checkbox"
                            checked={t.selected}
                            onChange={() => handleTableToggle(idx)}
                          />
                          <span className="db-table-name">{t.table}</span>
                          <span className="db-table-col-count">{t.columns.length}개 컬럼</span>
                        </label>
                        {t.selected && (
                          <Button
                            size='sm'
                            onClick={() => { setEditingTable(`${t.schema}.${t.table}`); if (!isEditing) setPhase(5); }}
                            appearance='outline'
                            color='gray'
                          >
                            {t.labelGenerated ? '✓ 설명 편집' : '한글 설명 설정'}
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          {/* 동기화 일정 */}
          <div className="form-item">
            <label className="form-label">동기화 일정</label>
            <Select
              className='pipe-settings-select'
              value={config.syncSchedule}
              onChange={e => set('syncSchedule', e.target.value as DbConfig['syncSchedule'])}
              options={[
                {value:'manual', label:'수동'},
                {value:'daily', label:'매일'},
                {value:'weekly', label:'매주'},
              ]}
            />
          </div>
        </div>
      </div>
    );
  };

  // ──────────────────────────────────────────
  // Phase 3: SQL 힌트 (신규 스텝 모드)
  // ──────────────────────────────────────────
  // 헤더 · 본문 구조는 Phase 2(테이블 선택)와 동일
  const renderPhase3Hints = () => (
    <div className="db-phase">
      <div className="db-phase-header d-flex justify-between">
        <div className='d-inline-flex items-center gap-4'>
          <button className="db-back-btn" onClick={() => setPhase(2)}>← 테이블 선택</button>
          <span className="db-phase-badge">3</span>
          <span className="db-phase-title">SQL 힌트</span>
          <div className="count-box">
            <span className='count'>{(config.hints ?? []).length}</span>개 등록됨
          </div>
        </div>
        <div className='db-next-btn-wrap'>
          <span>(선택)</span>
          <button className="db-next-btn" onClick={() => setPhase(4)}>
            SQL 프롬프트 →
          </button>
        </div>
      </div>
      <div className='db-form'>{renderHints()}</div>
    </div>
  );

  // ──────────────────────────────────────────
  // Phase 4: SQL 프롬프트 (신규 스텝 모드)
  // ──────────────────────────────────────────
  const renderPhase4Prompts = () => (
    <div className="db-phase">
      <div className="db-phase-header">
        <button className="db-back-btn" onClick={() => setPhase(3)}>← SQL 힌트</button>
        <span className="db-phase-badge">4</span>
        <span className="db-phase-title">SQL 프롬프트</span>
      </div>
      <div className='db-form'>{renderPrompts()}</div>
    </div>
  );

  // ──────────────────────────────────────────
  // 힌트 + 프롬프트 섹션 (탭에서 재사용)
  // ──────────────────────────────────────────

  const renderHints = () => (
    <>
      {/* 제목 줄 - 테이블 선택 탭과 같은 모양 (제목 + 개수 / 오른쪽 버튼) */}
      <div className="db-phase-header">
        <div className='db-phase-title-wrap'>
          <h4 className="db-phase-title">
            SQL 힌트
            <span className="optional ml-4">(선택)</span>
          </h4>
          <p className='db-phase-subtitle'>
            각 힌트는 별도 문서로 임베딩됩니다. 설명에 "어떤 질문에 쓰는 쿼리인지" 적으면 관련 질문에만 검색됩니다.
          </p>
        </div>
        <div className="db-phase-header-actions">
          <Button
            size="sm"
            appearance="fill"
            color="primary"
            onClick={() => set('hints', [...(config.hints ?? []), { description: '', sql: '' }])}
          >
            힌트 추가
          </Button>
        </div>
      </div>
      {(config.hints ?? []).length === 0 ? (
        <div className="db-hint-empty-box">
          힌트가 없습니다. 자주 쓰는 쿼리 패턴을 추가하면 LLM이 참고합니다.
        </div>
      ) : (
        (config.hints ?? []).map((hint: SqlHint, idx: number) => (
          <div key={idx} className="db-hint-card">
            <div className="db-hint-card-header">
              <input type="text" className="db-hint-desc-input w-100" placeholder="예: 영업담당자별 DF 건수 조회"
                value={hint.description}
                onChange={e => { const next = [...(config.hints ?? [])]; next[idx] = { ...hint, description: e.target.value }; set('hints', next); }}
              />
              <button className="db-hint-del-btn" onClick={() => set('hints', (config.hints ?? []).filter((_: SqlHint, i: number) => i !== idx))} title="삭제">✕</button>
            </div>
            <textarea className="db-hint-sql-input" rows={4}
              placeholder={`SELECT c.sales, COUNT(d.df_idx) as df_count\nFROM tb_customer c\nJOIN tb_aceinfo_detail d ON c.customerIdx = d.customerIdx\nWHERE c.sales = '홍길동'\nGROUP BY c.sales;`}
              value={hint.sql}
              onChange={e => { const next = [...(config.hints ?? [])]; next[idx] = { ...hint, sql: e.target.value }; set('hints', next); }}
            />
          </div>
        ))
      )}
    </>
  );

  const renderPrompts = () => (
    <div>
      {/* SQL 생성 프롬프트 */}
      <div className="form-item" style={{ marginBottom: '20px' }}>
        <label className="form-label">SQL 생성 프롬프트</label>
        <textarea className="pipe-settings-input" rows={7}
          value={config.sqlSystemPrompt ?? DEFAULT_SQL_SYSTEM_PROMPT}
          onChange={e => set('sqlSystemPrompt', e.target.value)}
        />
      </div>
      {/* 결과 요약 프롬프트 */}
      <div className="form-item">
        <label className="form-label">결과 요약 프롬프트</label>
        <textarea className="pipe-settings-input" rows={7}
          value={config.sqlSummaryPrompt ?? DEFAULT_SQL_SUMMARY_PROMPT}
          onChange={e => set('sqlSummaryPrompt', e.target.value)}
        />
      </div>
    </div>
  );

  // ──────────────────────────────────────────
  // Phase 5: 컬럼 한글 설명 편집 (신규 스텝 모드)
  // ──────────────────────────────────────────
  const renderPhase3 = () => {
    if (!editingTable) return null;
    const [editSchema, editTableName] = editingTable.split('.') as [string, string];
    const tableIdx = config.tables.findIndex(t => t.schema === editSchema && t.table === editTableName);
    if (tableIdx < 0) return null;
    const table = config.tables[tableIdx];
    const isGenerating = generatingLabels.has(editingTable);

    return (
      <div className="db-phase">
        <div className="db-phase-header">
          <Button size='sm' appearance='outline' color='gray' onClick={() => { setEditingTable(null); if (!isEditing) setPhase(2); }}>← 테이블 선택</Button>
          <span className="db-phase-badge">5</span>
            <div className='db-phase-title'>{table.table}</div>
            <p className='db-phase-subtitle'>✦ AI 자동생성 후 틀린 항목만 수정하세요. 포함 해제된 컬럼은 KB에서 제외됩니다</p>
          <button
            className={`db-generate-btn${isGenerating ? ' loading' : ''}`}
            onClick={() => handleGenerateLabels(editSchema, editTableName)}
            disabled={isGenerating}
          >
            {isGenerating
              ? <><span className="db-spinner" /> 생성 중...</>
              : table.labelGenerated
              ? '↺ 재생성'
              : '✦ AI 한글 설명 자동생성'}
          </button>
        </div>

        {/* 컬럼 테이블 */}
        <div className="table-wrap db-col-table-wrap">
          <table className="db-col-table w-100">
            <colgroup>
              <col width={60} />
              <col />
              <col width={100} />
              <col />
              <col width={140} />
            </colgroup>
            <thead>
              <tr>
                <th>포함</th>
                <th>컬럼명</th>
                <th>타입</th>
                <th>한글 설명</th>
                <th>DB 코멘트</th>
              </tr>
            </thead>
            <tbody>
              {table.columns.map((col, ci) => (
                <tr key={col.name} className={col.include ? '' : 'db-col-excluded'}>
                  <td className="db-col-check">
                    <input
                      type="checkbox"
                      checked={col.include}
                      onChange={e => handleIncludeChange(tableIdx, ci, e.target.checked)}
                    />
                  </td>
                  <td className="db-col-name">
                    <code>{col.name}</code>
                    {!col.nullable && <span className="db-col-required" title="NOT NULL">*</span>}
                  </td>
                  <td className="db-col-type">{col.type}</td>
                  <td className="db-col-label">
                    <input
                      type="text"
                      className="db-col-label-input"
                      placeholder="한글 설명 입력..."
                      value={col.label}
                      onChange={e => handleLabelChange(tableIdx, ci, e.target.value)}
                      disabled={!col.include}
                    />
                  </td>
                  <td className="db-col-comment">
                    {col.comment
                      ? <span className="d-block ellipsis" title={col.comment}>{col.comment}</span>
                      : <span className="db-col-comment-none">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="db-col-footer">
          <Button size='md' onClick={() => { setEditingTable(null); if (!isEditing) setPhase(2); }}>
            적용 후 목록 이동
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="pipe-tab-content">
      {/* ── 수정 모드: 탭 UI ── */}
      {isEditing ? (
        <>
          {/* 탭 헤더 (공통 Tabs) */}
          <Tabs<EditTab>
            items={[
              { key: 'connection', label: '연결 정보' },
              { key: 'tables',     label: '테이블 선택' },
              { key: 'hints',      label: `SQL 힌트${(config.hints ?? []).length > 0 ? ` (${config.hints.length})` : ''}` },
              { key: 'prompts',    label: 'SQL 프롬프트' },
            ]}
            active={activeTab}
            onChange={setActiveTab}
          />
          {/* 탭 콘텐츠 */}
          <div className="db-edit-tab-content">
            {activeTab === 'connection' && renderPhase1()}
            {activeTab === 'tables'     && (
              <>
                {/* 편집 중인 테이블이 있으면 컬럼 설명 편집 UI 표시 */}
                {editingTable ? renderPhase3() : renderPhase2()}
              </>
            )}
            {activeTab === 'hints'      && <>{renderHints()}</>}
            {activeTab === 'prompts'    && <>{renderPrompts()}</>}
          </div>
        </>
      ) : (
        /* ── 신규 모드: 기존 스텝 UI ── */
        <>
          {phase === 1 && renderPhase1()}
          {phase === 2 && renderPhase2()}
          {phase === 3 && renderPhase3Hints()}
          {phase === 4 && renderPhase4Prompts()}
          {phase === 5 && renderPhase3()}
        </>
      )}
    </div>
  );
}
